import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUserRoles } from '@/hooks/useUserRoles';
import { ArrowLeft, Users, Trophy, BookOpen, TrendingUp, Award, Calendar, Filter } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DashboardSkeleton } from '@/components/ui/dashboard-skeleton';

interface TeamProgress {
  team_id: string;
  team_name: string;
  total_members: number;
  completed_trainings: number;
  average_score: number;
  gold_badges: number;
  silver_badges: number;
  bronze_badges: number;
}

interface UserProgress {
  user_id: string;
  user_name: string;
  team_name: string;
  completions_count: number;
  average_score: number;
  latest_completion: string;
  badge_counts: {
    gold: number;
    silver: number;
    bronze: number;
  };
}

interface ModuleStats {
  module_id: string;
  module_title: string;
  total_assignments: number;
  completed_count: number;
  average_score: number;
  completion_rate: number;
}

const TrainingProgressDashboard = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isAdmin, isLoading: isLoadingRoles } = useUserRoles();
  const [teamProgress, setTeamProgress] = useState<TeamProgress[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgress[]>([]);
  const [moduleStats, setModuleStats] = useState<ModuleStats[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('all');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('30');
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState<any[]>([]);

  useEffect(() => {
    if (!isLoadingRoles && isAdmin) {
      fetchData();
    } else if (!isLoadingRoles && !isAdmin) {
      navigate('/training');
    }
  }, [isLoadingRoles, isAdmin, selectedTeam, selectedPeriod]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch teams for filter
      const { data: teamsData } = await supabase
        .from('teams')
        .select('id, name')
        .order('name');
      setTeams(teamsData || []);

      // Calculate date filter
      const dateFilter = new Date();
      dateFilter.setDate(dateFilter.getDate() - parseInt(selectedPeriod));

      // Fetch team progress
      await fetchTeamProgress(dateFilter);
      
      // Fetch user progress
      await fetchUserProgress(dateFilter);
      
      // Fetch module statistics
      await fetchModuleStats(dateFilter);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamProgress = async (dateFilter: Date) => {
    let query = supabase
      .from('training_completions')
      .select(`
        training_modules (team_id, title_en),
        badge_level,
        quiz_score,
        profiles (id, full_name),
        teams (id, name)
      `)
      .gte('completed_at', dateFilter.toISOString());

    if (selectedTeam !== 'all') {
      query = query.eq('training_modules.team_id', selectedTeam);
    }

    const { data } = await query;
    
    // Process team progress data
    const teamMap = new Map<string, TeamProgress>();
    
    data?.forEach((completion: any) => {
      const teamId = completion.training_modules?.team_id;
      const teamName = completion.teams?.name || 'Unknown Team';
      
      if (!teamMap.has(teamId)) {
        teamMap.set(teamId, {
          team_id: teamId,
          team_name: teamName,
          total_members: 0,
          completed_trainings: 0,
          average_score: 0,
          gold_badges: 0,
          silver_badges: 0,
          bronze_badges: 0,
        });
      }
      
      const progress = teamMap.get(teamId)!;
      progress.completed_trainings++;
      
      if (completion.badge_level === 'gold') progress.gold_badges++;
      else if (completion.badge_level === 'silver') progress.silver_badges++;
      else if (completion.badge_level === 'bronze') progress.bronze_badges++;
    });

    setTeamProgress(Array.from(teamMap.values()));
  };

  const fetchUserProgress = async (dateFilter: Date) => {
    const { data } = await supabase
      .from('training_completions')
      .select(`
        user_id,
        quiz_score,
        badge_level,
        completed_at,
        training_modules (title_en),
        profiles (full_name)
      `)
      .gte('completed_at', dateFilter.toISOString())
      .order('completed_at', { ascending: false });

    // Process user progress data
    const userMap = new Map<string, UserProgress>();
    
    data?.forEach((completion: any) => {
      const userId = completion.user_id;
      const userName = completion.profiles?.full_name || 'Unknown User';
      
      if (!userMap.has(userId)) {
        userMap.set(userId, {
          user_id: userId,
          user_name: userName,
          team_name: 'N/A',
          completions_count: 0,
          average_score: 0,
          latest_completion: completion.completed_at,
          badge_counts: { gold: 0, silver: 0, bronze: 0 }
        });
      }
      
      const progress = userMap.get(userId)!;
      progress.completions_count++;
      
      if (completion.badge_level === 'gold') progress.badge_counts.gold++;
      else if (completion.badge_level === 'silver') progress.badge_counts.silver++;
      else if (completion.badge_level === 'bronze') progress.badge_counts.bronze++;
    });

    setUserProgress(Array.from(userMap.values()));
  };

  const fetchModuleStats = async (dateFilter: Date) => {
    const { data } = await supabase
      .from('training_modules')
      .select(`
        id,
        title_en,
        training_completions!inner (
          quiz_score,
          completed_at
        )
      `)
      .gte('training_completions.completed_at', dateFilter.toISOString());

    const moduleStatsData: ModuleStats[] = data?.map((module: any) => ({
      module_id: module.id,
      module_title: module.title_en,
      total_assignments: 0, // Would need to join with assignments table
      completed_count: module.training_completions.length,
      average_score: module.training_completions.reduce((sum: number, c: any) => sum + c.quiz_score, 0) / module.training_completions.length || 0,
      completion_rate: 0 // Would calculate based on assignments
    })) || [];

    setModuleStats(moduleStatsData);
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (!isAdmin) {
    return null;
  }

  const totalCompletions = userProgress.reduce((sum, user) => sum + user.completions_count, 0);
  const totalGoldBadges = teamProgress.reduce((sum, team) => sum + team.gold_badges, 0);
  const totalSilverBadges = teamProgress.reduce((sum, team) => sum + team.silver_badges, 0);
  const totalBronzeBadges = teamProgress.reduce((sum, team) => sum + team.bronze_badges, 0);

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/training')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {language === 'en' ? 'Back' : 'Πίσω'}
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {language === 'en' ? '📊 Training Progress Dashboard' : '📊 Πίνακας Προόδου Εκπαίδευσης'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {language === 'en' 
              ? 'Monitor team training progress, completion rates, and performance metrics'
              : 'Παρακολουθήστε την πρόοδο εκπαίδευσης ομάδων, ποσοστά ολοκλήρωσης και μετρικές απόδοσης'
            }
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4" />
          <Select value={selectedTeam} onValueChange={setSelectedTeam}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select Team" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.id}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 Days</SelectItem>
              <SelectItem value="30">30 Days</SelectItem>
              <SelectItem value="90">90 Days</SelectItem>
              <SelectItem value="365">1 Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="flex items-center p-6">
            <BookOpen className="h-8 w-8 text-blue-500 mr-4" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Completions</p>
              <p className="text-2xl font-bold">{totalCompletions}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="flex items-center p-6">
            <Trophy className="h-8 w-8 text-yellow-500 mr-4" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">Gold Badges</p>
              <p className="text-2xl font-bold">{totalGoldBadges}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="flex items-center p-6">
            <Award className="h-8 w-8 text-gray-400 mr-4" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">Silver Badges</p>
              <p className="text-2xl font-bold">{totalSilverBadges}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-6">
            <Award className="h-8 w-8 text-orange-600 mr-4" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">Bronze Badges</p>
              <p className="text-2xl font-bold">{totalBronzeBadges}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Team Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {teamProgress.map((team) => (
              <div key={team.team_id} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h3 className="font-medium">{team.team_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {team.completed_trainings} completions
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-yellow-600">
                    🥇 {team.gold_badges}
                  </Badge>
                  <Badge variant="outline" className="text-gray-600">
                    🥈 {team.silver_badges}
                  </Badge>
                  <Badge variant="outline" className="text-orange-600">
                    🥉 {team.bronze_badges}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Top Performers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Top Performers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {userProgress
              .sort((a, b) => b.completions_count - a.completions_count)
              .slice(0, 10)
              .map((user) => (
                <div key={user.user_id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-medium">{user.user_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {user.completions_count} completions
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-yellow-600">
                      🥇 {user.badge_counts.gold}
                    </Badge>
                    <Badge variant="outline" className="text-gray-600">
                      🥈 {user.badge_counts.silver}
                    </Badge>
                    <Badge variant="outline" className="text-orange-600">
                      🥉 {user.badge_counts.bronze}
                    </Badge>
                  </div>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TrainingProgressDashboard;